import _ from 'lodash';
import traverse from 'traverse';
import { getProxiedServiceUrl } from '@xengage/gw-portals-url-js';
import { JsonRPCService } from '@xengage/gw-portals-transport-js';
import { DocumentDownloadService } from '@xengage/gw-portals-document-js';
import { MLMultiLobsDataHolderService } from '../../ml-services-common-react';
import MLQuotePaymentIDsService from './MLQuotePaymentIDsService';
import Submission from '../models/Submission';

function generateTempID(data) {
    const tempIDPaths = traverse(data).paths()
        .filter((pathArray) => _.last(pathArray) === 'tempId')
        .map((pathArray) => pathArray.join('.'));

    return tempIDPaths.reduce((acc, path) => {
        acc[path] = _.get(data, path);
        return acc;
    }, {});
}

function setTempIDs(tempIDMap, data) {
    Object.entries(tempIDMap).forEach(([tempIDPath, tempIDValue]) => {
        _.set(data, tempIDPath, tempIDValue);
    });
}

/**
 * Invokes the given method on the backend endpoint passing the given data as parameters
 *
 * @param {any} method the method to invoke on the backend endpoint
 * @param {any} data the data to be passed as part of the invokation on the backend
 * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
 * @returns {Promise} the promise from the backend call
 */
function processSubmission(method, data, additionalHeaders = {}) {
    return JsonRPCService.send(getProxiedServiceUrl('quote'), method, data, additionalHeaders).then(
        (response) => {
            if (response && response.baseData) {
                return new Submission(response);
            }
            return response;
        }
    );
}

function startMultiLobsJobFromSubmission(submission, additionalHeaders = {}) {
    MLMultiLobsDataHolderService.startJob(
        submission.baseData.accountNumber,
        submission.quoteID,
        submission.baseData.productCode,
        submission.baseData.accountHolder.primaryAddress.postalCode,
        additionalHeaders
    );
}

/**
 * This is the server responsible for dealing with the Quote and Buy backend operations
 *
 * @export
 * @class LoadSaveService
 */
export default class LoadSaveService {
    /**
     * Creates new submission job and generates a session to be used during the submission process.
     *
     * @param {Object} data the submission
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise}
     * @memberof LoadSaveService
     */
    static createSubmission(data, additionalHeaders = {}) {
        return processSubmission('create', [data], additionalHeaders).then((result) => {
            startMultiLobsJobFromSubmission(result, additionalHeaders);
            return result;
        });
    }

    /**
     * Updates, saves and performs a quote on an existing submission.
     * Generates quotes for all available product offerings.
     *
     * @param {Object} data tge submission that will be saved
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static saveAndQuoteSubmission(data, additionalHeaders = {}) {
        const dataCopy = { ...data };

        if (dataCopy.bindData) {
            delete dataCopy.bindData;
        }

        if (dataCopy.quoteData) {
            delete dataCopy.quoteData;
        }

        const productName = _.camelCase(_.get(dataCopy, 'baseData.productName', undefined));
        if (productName !== undefined) {
            // In V9 this information was part of the quoteData, this needs to be deleted separately
            _.set(dataCopy, `lobData.${productName}.offerings`, []);
        }

        _.set(data, 'additionalPaymentQuoteRequests_ML',
            MLQuotePaymentIDsService.getAvailablePaymentPlanIDs(data));
        return processSubmission('saveAndQuote', [data], additionalHeaders);
    }

    /**
     * Updates an existing draft submission.
     *
     * @param {Object} data the submission to be saved as draft
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static updateDraftSubmission(data, additionalHeaders = {}) {
        const tempIDs = generateTempID(data);
        data.beforeSave();
        if (data.bindData) {
            delete data.bindData;
        }
        if (data.quoteData) {
            delete data.quoteData;
        }

        const lobPathString = _.camelCase(_.get(data, 'baseData.productName', undefined));
        if (lobPathString !== undefined) {
            _.set(data, `lobData.${lobPathString}.offerings`, []);
        }

        return processSubmission('updateDraftSubmission', [data], additionalHeaders).then((result) => {
            startMultiLobsJobFromSubmission(result, additionalHeaders);
            setTempIDs(tempIDs, result);
            return result;
        });
    }

    /**
     * Updates a quoted submission.
     *
     * @param {Object} data the submission to be saved as draft
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static updateQuotedSubmission(data, additionalHeaders = {}) {
        return processSubmission('updateQuotedSubmission', [data], additionalHeaders).then((result) => {
            startMultiLobsJobFromSubmission(result, additionalHeaders);
            return result;
        });
    }

    /**
     * Updates a quoted submission with LOB Data.
     *
     * @param {Object} data the submission to be saved as draft
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static updateQuotedSubmissionWithLOBData(data, additionalHeaders = {}) {
        return processSubmission('updateQuotedSubmissionWithLOBData', [data], additionalHeaders);
    }

    /**
     * Retrieves the payment plans for the current submission.
     *
     * @param {string} quoteID quoteID of submission that needs to be found
     * @param {string} sessionUUID current session id
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static retrievePaymentPlans(quoteID, sessionUUID, additionalHeaders = {}) {
        return processSubmission(
            'retrievePaymentPlans',
            [quoteID, sessionUUID],
            additionalHeaders
        );
    }

    /**
     * Updates an existing draft submission and account primary location.
     * @param {Object} submission the submission
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static updateDraftSubmissionAndPrimaryLocation(submission, additionalHeaders = {}) {
        const tempIDs = generateTempID(submission);
        return processSubmission(
            'updateDraftSubmissionAndPrimaryLocation',
            [submission],
            additionalHeaders
        ).then((result) => {
            setTempIDs(tempIDs, result);
            return result;
        });
    }

    static updateCoverages(quoteID, sessionUUID, lobCoverages, additionalHeaders = {}) {
        return processSubmission(
            'updateCoverages',
            [quoteID, sessionUUID, lobCoverages],
            additionalHeaders
        );
    }

    static setQuoteToDraft(quoteID, sessionUUID, additionalHeaders = {}) {
        return processSubmission(
            'setQuoteToDraft',
            [quoteID, sessionUUID],
            additionalHeaders
        );
    }

    /**
     * Binds the submission.
     *
     * @param {Object} data the submission to be bound
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static bindSubmission(data, additionalHeaders = {}) {
        return processSubmission('bind', [data], additionalHeaders);
    }

    /**
     * Retrieves a submission.
     *
     * @param {Object} data the retrieval payload (QuoteRetrievalDTO)
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static retrieveSubmission(data, additionalHeaders = {}) {
        return processSubmission('retrieve', [data], additionalHeaders).then((submission) => {
            MLMultiLobsDataHolderService.retrieveFromPortalStorage(submission.baseData.accountNumber, submission.quoteID);
            return submission;
        });
    }

    /**
     * Get the address from postalcode.
     *
     * @param {Number} submission the submission
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise}
     * @memberof LoadSaveService
     */
    static createBaseOffering(submission, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('slquote'),
            'createBaseOffering',
            [submission],
            additionalHeaders
        );
    }

    /**
     * Get Email of Quote with its number.
     *
     * @param {Number} submission the submission
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise}
     * @memberof LoadSaveService
     */
    static getEmailQuote(submission, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('quote'),
            'emailQuote',
            [submission],
            additionalHeaders
        );
    }

    /**
     * Update modifiers.
     *
     * @param {Object} data the submissionDTO
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static updateModifiers(data, additionalHeaders = {}) {
        return processSubmission('updateModifiers', [data], additionalHeaders);
    }

    static selectOffering(data, additionalHeaders = {}) {
        return processSubmission('selectOffering', [data], additionalHeaders);
    }

    static getSubmissionDetails(data, additionalHeaders = {}) {
        return processSubmission('getSubmissionDetails', [data], additionalHeaders);
    }

    static getPaymentInstruments(quoteID, sessionUUID, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('quote'),
            'getPaymentInstruments',
            [quoteID, sessionUUID],
            additionalHeaders
        );
    }

    static isPeriodBound(data, additionalHeaders = {}) {
        return processSubmission('isPeriodBound', [data], additionalHeaders);
    }

    static syncQuestionSetML(quoteID, qsDto) {
        return processSubmission('syncQuestionSetML', [quoteID, qsDto]);
    }

    static syncLocationQuestionSet(quoteID, locationID, qsDto, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('quote'),
            'syncLocationQuestionSet',
            [quoteID, locationID, qsDto],
            additionalHeaders
        );
    }

    static getJobData(sessionUUID, quoteID) {
        return processSubmission('getJobData', [sessionUUID, quoteID]);
    }

    static takeOverSubmission(quoteID, zipCode, additionalHeaders = {}) {
        if (zipCode && zipCode.length > 5) {
            zipCode = zipCode.substr(0, 5);
        }
        return processSubmission('takeOver', [quoteID, zipCode], additionalHeaders);
    }

    static preBind (submissionDTO) {
        return processSubmission('preBind', [submissionDTO]);
    }

    static saveSubmissionAndSyncAccount(data, additionalHeaders = {}) {
        return processSubmission('saveSubmissionAndSyncAccountForCompRater', [data], additionalHeaders).then((result) => {
            startMultiLobsJobFromSubmission(result, additionalHeaders);
            return result;
        });
    }

    static resolveHVQDiscrepancies(data) {
        return processSubmission('resolveHVQDiscrepancies', [data]);
    }

    static HOShouldDisplayAnimalExclusionWarning(quoteID, additionalHeaders = {}) {
        return processSubmission('HOShouldDisplayAnimalExclusionWarning', [quoteID], additionalHeaders);
    }

    static getNamedInsuredCandidatesML(accountNumber, productCode, additionalHeaders = {}) {
        return processSubmission('getNamedInsuredCandidates', [accountNumber, productCode], additionalHeaders);
    }

    static getAdditionalInterestCandidatesML(quoteID, additionalHeaders = {}) {
        return processSubmission('getAdditionalInterestCandidates', [quoteID], additionalHeaders);
    }

    static checkForBlockIssuanceUwIssues(quoteID, additionalHeaders = {}) {
        return processSubmission('checkForBlockIssuanceUwIssues', [quoteID], additionalHeaders);
    }

    static assignPolicyNumbersToModifiers(data, additionalHeaders = {}) {
        return processSubmission('assignPolicyNumbersToModifiers_Ext', [data.quoteID, data.sessionUUID, data.portalCurrentStep_ML], additionalHeaders);
    }

    static getAccountDetails(accountNumber, additionalHeaders = {}) {
        return processSubmission('getAccountDetails', [accountNumber], additionalHeaders);
    }

    static createQuoteProposal(quoteID, periodID, isBundled, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('quote'),
            'createQuoteProposal',
            [quoteID, periodID, isBundled],
            additionalHeaders
        );
    }

    static downloadDocument(documentID, token) {
            return DocumentDownloadService.getDocumentLink(getProxiedServiceUrl('downloadDocument'), documentID, token);
    }

    static sendHomeInspectionOrder(quoteID, additionalHeaders = {}) {
        return processSubmission('sendHomeInspectionOrder_Ext', [quoteID], additionalHeaders);
    }

    static getPropertyValuationQB(data, quoteID) {
        return processSubmission('propertyValuationQB', [data, quoteID]);
    }
}
